import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../lib/supabase';
import { useAuth } from './AuthContext';

const PVDataContext = createContext();

export const usePVData = () => {
  const context = useContext(PVDataContext);
  if (!context) {
    throw new Error('usePVData must be used within a PVDataProvider');
  }
  return context;
};

export const PVDataProvider = ({ children }) => {
  const [plants, setPlants] = useState([]);
  const [monthlyData, setMonthlyData] = useState({});
  const [loading, setLoading] = useState(true);
  const { user, isAuthenticated } = useAuth();

  // Load plants and monthly data from Supabase
  useEffect(() => {
    const loadData = async () => {
      if (!isAuthenticated || !user) {
        // Use localStorage data if not authenticated
        const savedPlants = localStorage.getItem('pv-plants');
        const savedMonthlyData = localStorage.getItem('pv-monthly-data');
        
        if (savedPlants) {
          setPlants(JSON.parse(savedPlants));
        }
        
        if (savedMonthlyData) {
          setMonthlyData(JSON.parse(savedMonthlyData));
        }
        
        setLoading(false);
        return;
      }

      setLoading(true);
      
      try {
        // Load plants
        const { data: plantsData, error: plantsError } = await supabase
          .from('pv_plants_a7b3c9d8e2')
          .select('*')
          .order('created_at', { ascending: false });

        if (plantsError) {
          console.error('Error loading plants:', plantsError);
          return;
        }

        // Transform plants data to match the expected format
        const transformedPlants = plantsData.map(plant => ({
          id: plant.id,
          name: plant.name,
          location: plant.location,
          identifier: plant.identifier,
          meterNumber: plant.meter_number,
          capacity: plant.capacity,
          stringCount: plant.string_count,
          inverterCount: plant.inverter_count,
          createdAt: plant.created_at
        }));

        setPlants(transformedPlants);

        // Load monthly data for all plants
        if (transformedPlants.length > 0) {
          const { data: monthlyDataList, error: monthlyDataError } = await supabase
            .from('pv_monthly_data_a7b3c9d8e2')
            .select('*')
            .in('plant_id', transformedPlants.map(plant => plant.id));

          if (monthlyDataError) {
            console.error('Error loading monthly data:', monthlyDataError);
            return;
          }

          // Transform monthly data to the expected format
          const monthlyDataMap = {};
          monthlyDataList.forEach(data => {
            const key = `${data.plant_id}-${data.year}-${data.month}`;
            monthlyDataMap[key] = parseFloat(data.production);
          });

          setMonthlyData(monthlyDataMap);
        }
      } catch (error) {
        console.error('Error in data loading:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isAuthenticated, user]);

  const addPlant = async (plantData) => {
    if (!isAuthenticated || !user) {
      // Fallback to local storage if not authenticated
      const newPlant = {
        ...plantData,
        id: Date.now().toString(),
        createdAt: new Date().toISOString()
      };
      const newPlants = [...plants, newPlant];
      setPlants(newPlants);
      localStorage.setItem('pv-plants', JSON.stringify(newPlants));
      return newPlant;
    }

    try {
      // Insert into Supabase
      const { data, error } = await supabase
        .from('pv_plants_a7b3c9d8e2')
        .insert({
          name: plantData.name,
          location: plantData.location,
          identifier: plantData.identifier,
          meter_number: plantData.meterNumber,
          capacity: parseFloat(plantData.capacity),
          string_count: parseInt(plantData.stringCount),
          inverter_count: parseInt(plantData.inverterCount),
          user_id: user.id
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding plant:', error);
        return null;
      }

      // Transform to match expected format
      const newPlant = {
        id: data.id,
        name: data.name,
        location: data.location,
        identifier: data.identifier,
        meterNumber: data.meter_number,
        capacity: data.capacity,
        stringCount: data.string_count,
        inverterCount: data.inverter_count,
        createdAt: data.created_at
      };

      setPlants(prev => [...prev, newPlant]);
      
      // Handle file uploads if any
      if (plantData.documents && plantData.documents.length > 0) {
        await Promise.all(plantData.documents.map(async (doc) => {
          // In a real implementation, you would upload the file to Supabase Storage
          // and then save the metadata to the documents table
          const { data: docData, error: docError } = await supabase
            .from('pv_documents_a7b3c9d8e2')
            .insert({
              plant_id: newPlant.id,
              name: doc.name,
              file_path: doc.url || '',
              file_type: doc.type || 'application/pdf',
              file_size: doc.size || 0
            });

          if (docError) {
            console.error('Error adding document:', docError);
          }
        }));
      }

      if (plantData.images && plantData.images.length > 0) {
        await Promise.all(plantData.images.map(async (img) => {
          // Similar to documents
          const { data: imgData, error: imgError } = await supabase
            .from('pv_images_a7b3c9d8e2')
            .insert({
              plant_id: newPlant.id,
              name: img.name,
              file_path: img.url || '',
              file_type: img.type || 'image/jpeg',
              file_size: img.size || 0
            });

          if (imgError) {
            console.error('Error adding image:', imgError);
          }
        }));
      }

      return newPlant;
    } catch (error) {
      console.error('Exception adding plant:', error);
      return null;
    }
  };

  const updatePlant = async (plantId, updatedPlant) => {
    if (!isAuthenticated || !user) {
      // Fallback to local storage if not authenticated
      const newPlants = plants.map(plant => 
        plant.id === plantId ? { ...plant, ...updatedPlant } : plant
      );
      setPlants(newPlants);
      localStorage.setItem('pv-plants', JSON.stringify(newPlants));
      return;
    }

    try {
      // Update in Supabase
      const { error } = await supabase
        .from('pv_plants_a7b3c9d8e2')
        .update({
          name: updatedPlant.name,
          location: updatedPlant.location,
          identifier: updatedPlant.identifier,
          meter_number: updatedPlant.meterNumber,
          capacity: parseFloat(updatedPlant.capacity),
          string_count: parseInt(updatedPlant.stringCount),
          inverter_count: parseInt(updatedPlant.inverterCount)
        })
        .eq('id', plantId);

      if (error) {
        console.error('Error updating plant:', error);
        return;
      }

      // Update local state
      setPlants(prev => prev.map(plant => 
        plant.id === plantId ? { 
          ...plant, 
          name: updatedPlant.name,
          location: updatedPlant.location,
          identifier: updatedPlant.identifier,
          meterNumber: updatedPlant.meterNumber,
          capacity: updatedPlant.capacity,
          stringCount: updatedPlant.stringCount,
          inverterCount: updatedPlant.inverterCount
        } : plant
      ));

      // Handle documents and images updates
      // This is simplified - in a real app, you'd need to handle deletions and additions
      if (updatedPlant.documents && updatedPlant.documents.length > 0) {
        // For simplicity, we're just handling new documents that don't have an ID yet
        const newDocs = updatedPlant.documents.filter(doc => !doc.id);
        await Promise.all(newDocs.map(async (doc) => {
          const { error: docError } = await supabase
            .from('pv_documents_a7b3c9d8e2')
            .insert({
              plant_id: plantId,
              name: doc.name,
              file_path: doc.url || '',
              file_type: doc.type || 'application/pdf',
              file_size: doc.size || 0
            });

          if (docError) {
            console.error('Error adding document:', docError);
          }
        }));
      }

      if (updatedPlant.images && updatedPlant.images.length > 0) {
        const newImages = updatedPlant.images.filter(img => !img.id);
        await Promise.all(newImages.map(async (img) => {
          const { error: imgError } = await supabase
            .from('pv_images_a7b3c9d8e2')
            .insert({
              plant_id: plantId,
              name: img.name,
              file_path: img.url || '',
              file_type: img.type || 'image/jpeg',
              file_size: img.size || 0
            });

          if (imgError) {
            console.error('Error adding image:', imgError);
          }
        }));
      }
    } catch (error) {
      console.error('Exception updating plant:', error);
    }
  };

  const deletePlant = async (plantId) => {
    if (!isAuthenticated || !user) {
      // Fallback to local storage if not authenticated
      const newPlants = plants.filter(plant => plant.id !== plantId);
      setPlants(newPlants);
      localStorage.setItem('pv-plants', JSON.stringify(newPlants));
      
      // Remove monthly data for this plant
      const newMonthlyData = { ...monthlyData };
      Object.keys(newMonthlyData).forEach(key => {
        if (key.startsWith(`${plantId}-`)) {
          delete newMonthlyData[key];
        }
      });
      setMonthlyData(newMonthlyData);
      localStorage.setItem('pv-monthly-data', JSON.stringify(newMonthlyData));
      return;
    }

    try {
      // Delete from Supabase - cascade deletes will handle related data
      const { error } = await supabase
        .from('pv_plants_a7b3c9d8e2')
        .delete()
        .eq('id', plantId);

      if (error) {
        console.error('Error deleting plant:', error);
        return;
      }

      // Update local state
      setPlants(prev => prev.filter(plant => plant.id !== plantId));
      
      // Remove monthly data for this plant from local state
      const newMonthlyData = { ...monthlyData };
      Object.keys(newMonthlyData).forEach(key => {
        if (key.startsWith(`${plantId}-`)) {
          delete newMonthlyData[key];
        }
      });
      setMonthlyData(newMonthlyData);
    } catch (error) {
      console.error('Exception deleting plant:', error);
    }
  };

  const updateMonthlyData = async (plantId, month, year, kwh) => {
    const key = `${plantId}-${year}-${month}`;
    
    if (!isAuthenticated || !user) {
      // Fallback to local storage if not authenticated
      const newMonthlyData = { ...monthlyData, [key]: kwh };
      setMonthlyData(newMonthlyData);
      localStorage.setItem('pv-monthly-data', JSON.stringify(newMonthlyData));
      return;
    }

    try {
      // Check if data exists
      const { data: existingData, error: checkError } = await supabase
        .from('pv_monthly_data_a7b3c9d8e2')
        .select('id')
        .eq('plant_id', plantId)
        .eq('month', month)
        .eq('year', year)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking monthly data:', checkError);
        return;
      }

      let error;
      
      if (existingData) {
        // Update existing record
        const { error: updateError } = await supabase
          .from('pv_monthly_data_a7b3c9d8e2')
          .update({
            production: kwh,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingData.id);
          
        error = updateError;
      } else {
        // Insert new record
        const { error: insertError } = await supabase
          .from('pv_monthly_data_a7b3c9d8e2')
          .insert({
            plant_id: plantId,
            month,
            year,
            production: kwh
          });
          
        error = insertError;
      }

      if (error) {
        console.error('Error updating monthly data:', error);
        return;
      }

      // Update local state
      setMonthlyData(prev => ({ ...prev, [key]: kwh }));
    } catch (error) {
      console.error('Exception updating monthly data:', error);
    }
  };

  const getMonthlyData = (plantId, month, year) => {
    const key = `${plantId}-${year}-${month}`;
    return monthlyData[key] || 0;
  };

  const getYearlyData = (plantId, year) => {
    let total = 0;
    for (let month = 1; month <= 12; month++) {
      total += getMonthlyData(plantId, month, year);
    }
    return total;
  };

  return (
    <PVDataContext.Provider value={{
      plants,
      addPlant,
      updatePlant,
      deletePlant,
      updateMonthlyData,
      getMonthlyData,
      getYearlyData,
      monthlyData,
      loading
    }}>
      {children}
    </PVDataContext.Provider>
  );
};